import { useState } from 'preact/hooks';
import type { Theme } from '../styles/theme.js';
import { STRINGS } from '../strings.js';
import { isValidEmail } from '../lib/email.js';

type Props = {
  variant: 'blocking' | 'soft';
  theme: Theme;
  onSubmit: (email: string, name: string) => void;
  onDismiss?: () => void;
  showPendingNote?: boolean;
};

export function ContactForm({ variant, theme, onSubmit, onDismiss, showPendingNote }: Props) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);

  const valid = isValidEmail(email);

  function handleSubmit(e: Event): void {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    onSubmit(email.trim(), name.trim());
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        padding: '10px 16px',
        backgroundColor: variant === 'soft' ? '#f8f9fa' : '#fff8e6',
        borderBottom: '1px solid #e8e8e8',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      {variant === 'soft' && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '12px', color: '#444' }}>{STRINGS.contactSoftInvite}</span>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label={STRINGS.contactDismiss}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#888',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <input
          type="email"
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          onBlur={() => setTouched(true)}
          placeholder={STRINGS.contactEmailPlaceholder}
          aria-label={STRINGS.contactEmailLabel}
          required
          style={{
            flex: '1 1 140px',
            padding: '8px 10px',
            borderRadius: '8px',
            border: touched && !valid ? '1px solid #d9534f' : '1px solid #ddd',
            // 16px minimum : sous ce seuil, Safari iOS zoome automatiquement
            // la page au focus du champ.
            fontSize: '16px',
            outline: 'none',
          }}
        />
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          placeholder={STRINGS.contactNamePlaceholder}
          aria-label={STRINGS.contactNameLabel}
          style={{
            flex: '1 1 100px',
            padding: '8px 10px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            fontSize: '16px',
            outline: 'none',
          }}
        />
        <button
          type="submit"
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            backgroundColor: theme.brandColor,
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          {STRINGS.contactSubmit}
        </button>
      </div>
      {touched && !valid && (
        <span style={{ fontSize: '11px', color: '#d9534f' }}>{STRINGS.contactInvalidEmail}</span>
      )}
      {showPendingNote && (
        <span style={{ fontSize: '11px', color: '#664d03' }}>{STRINGS.contactPendingMessage}</span>
      )}
    </form>
  );
}
